import {FC, useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {useAuth} from '../../../../app/modules/auth'
import {useQuery} from 'react-query'
import axios from 'axios'
import {getAuth} from '../../../../app/modules/auth/core/AuthHelpers'
import {Link, useLocation} from 'react-router-dom'
import {TaskModal} from '../../modals/task/TaskModal'

const API_URL = process.env.REACT_APP_THEME_API_URL

interface RushMindNudge {
  id: number
  signal_key: string
  target_label: string
  target_url: string
}

interface RushMindInsight {
  id: number
  insight: string
  type: string
  type_label: string
  insight_date: string
  generated_at: string
  is_onboarding?: boolean
  // Cobranças abertas ligadas a este insight. Pode vir undefined em respostas antigas em cache.
  open_nudges?: RushMindNudge[]
  // Recap semanal ("sua semana"). Só vem preenchido na segunda, depois do job rushmind:weekly-recap.
  // Pode vir undefined/null em respostas antigas em cache ou fora de segunda-feira.
  weekly_recap?: RushMindWeeklyRecap | null
}

interface RushMindWeeklyRecap {
  text: string
  current_streak: number
  best_streak: number
}

// Chave do localStorage que marca o recap de HOJE como já visto pelo usuário neste navegador.
const weeklyRecapSeenKey = (): string =>
  `rushmind_weekly_recap_seen_${new Date().toISOString().split('T')[0]}`

const hasSeenWeeklyRecap = (): boolean => {
  try {
    return localStorage.getItem(weeklyRecapSeenKey()) === '1'
  } catch {
    return false
  }
}

// Estado local por cobrança enquanto o usuário interage com os botões "Já resolvi" / "Ignorar".
interface NudgeLocalState {
  loading?: boolean
  message?: string
  removed?: boolean
}

interface RushMindAvailability {
  success: boolean
  should_show_rushmind: boolean
  has_rushmind_data?: boolean
  has_rushmind_module?: boolean
  reason?: string
}

// Funções de API (mesmas do dashboard)
const checkRushMindAvailability = async () => {
  const response = await axios.get(`${API_URL}/api/ai-insights/check-availability`)
  return response.data
}

const getRushMindInsight = async () => {
  // Configurar token manualmente
  const auth = getAuth()
  if (auth?.api_token) {
    (axios.defaults.headers as any).Authorization = `Bearer ${auth.api_token}`
  }
  
  const response = await axios.get(`${API_URL}/api/ai-insights/rushmind`)
  return response.data
}

// O texto do insight vem do LLM e pode citar, verbatim, nomes de projeto/cliente/pessoa
// cadastrados por qualquer usuário do sistema (dado não confiável). Como isso é injetado via
// dangerouslySetInnerHTML mais abaixo, escapamos entidades HTML ANTES de aplicar as
// transformações de markdown, pra um nome malicioso (ex: um projeto chamado
// "<img src=x onerror=alert(1)>") virar texto inerte em vez de HTML executável.
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

// Só vira link/botão de verdade se a URL for um caminho interno relativo (ex: /projects/123).
// Bloqueia esquemas perigosos (javascript:, data:) e links "protocol-relative" (//host) que
// sairiam do domínio da aplicação — mesmo que hoje toda URL venha de dado nosso (id numérico),
// isso não depende de o back-end nunca errar nem do LLM nunca inventar uma URL por conta própria.
const isSafeInternalPath = (url: string): boolean => /^\/(?!\/)[^\s"'<>]*$/.test(url)

// Função para processar Markdown e criar botões clicáveis para insights de onboarding
const processMarkdown = (text: string, isOnboarding: boolean = false): string => {
  let processed = escapeHtml(text)

  const renderLink = (linkText: string, url: string, tag: 'button' | 'a', bold: boolean): string => {
    const inner = bold ? `<strong>${linkText}</strong>` : linkText
    if (!isSafeInternalPath(url)) {
      return inner
    }
    return tag === 'button'
      ? `<button class="rushmind-button" data-href="${url}">${inner}</button>`
      : `<a href="${url}" class="rushmind-link">${inner}</a>`
  }

  if (isOnboarding) {
    // Para insights de onboarding, processa LINKS PRIMEIRO (antes de bold)
    // Links com texto em negrito: [**texto**](url)
    processed = processed.replace(/\[\*\*(.+?)\*\*\]\((.+?)\)/g, (_m, p1, p2) => renderLink(p1, p2, 'button', true))
    // Links normais: [texto](url). Usa .+? (não [^\]]+?) porque o texto do link pode ser um
    // nome que já contém colchetes (ex: projeto "[DLOA] Nome"); o regex para no primeiro "](" encontrado.
    processed = processed.replace(/\[(.+?)\]\((.+?)\)/g, (_m, p1, p2) => renderLink(p1, p2, 'button', false))
  } else {
    // Para insights normais, processa LINKS PRIMEIRO (antes de bold)
    // Links com texto em negrito: [**texto**](url)
    processed = processed.replace(/\[\*\*(.+?)\*\*\]\((.+?)\)/g, (_m, p1, p2) => renderLink(p1, p2, 'a', true))
    // Links normais: [texto](url). Usa .+? (não [^\]]+?) porque o texto do link pode ser um
    // nome que já contém colchetes (ex: projeto "[DLOA] Nome"); o regex para no primeiro "](" encontrado.
    processed = processed.replace(/\[(.+?)\]\((.+?)\)/g, (_m, p1, p2) => renderLink(p1, p2, 'a', false))
  }

  // Converte **texto** para <strong>texto</strong> (DEPOIS dos links)
  processed = processed.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  
  return processed
}

const RushMind: FC = () => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const location = useLocation()

  // No dashboard do colaborador, o bloco RushMind inline (score + cobranças) já lista as
  // cobranças abertas. Esconder a lista aqui no widget flutuante evita a duplicação.
  // O widget segue mostrando saudação, insight do dia e feedback.
  const hideNudgesForInlineSection =
    location.pathname.startsWith('/dashboard') &&
    !currentUser?.administrator &&
    !currentUser?.manager
  
  // Chave única do localStorage para o estado de minimizado
  const STORAGE_KEY = 'rushmind_minimized_state'
  
  // Estado inicial: verifica se estava minimizado anteriormente
  const [isMinimized, setIsMinimized] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return saved ? JSON.parse(saved).isMinimized : false
    } catch {
      return false
    }
  })
  
  const [feedback, setFeedback] = useState<'helpful' | 'not_helpful' | null>(null)
  // Motivo opcional do 👎 (feedback universal do RushMind, mesmo dado do Discord)
  const [feedbackReason, setFeedbackReason] = useState('')
  const [feedbackReasonSent, setFeedbackReasonSent] = useState(false)
  const [nudgeState, setNudgeState] = useState<Record<number, NudgeLocalState>>({})
  const [nudgeSuccess, setNudgeSuccess] = useState<string | null>(null)
  const [sessionTimestamp] = useState(Date.now())
  const [showTaskModal, setShowTaskModal] = useState(false)
  // Recap semanal já dispensado hoje neste navegador: começa lendo o localStorage.
  const [weeklyRecapSeen, setWeeklyRecapSeen] = useState<boolean>(() => hasSeenWeeklyRecap())

  // Estado para controlar quando a página principal está carregada
  // Prioriza o carregamento do conteúdo principal antes de gerar o insight
  const [isPageReady, setIsPageReady] = useState(false)
  
  // Estado inicial: recupera o último ID do insight do localStorage
  const [lastInsightId, setLastInsightId] = useState<number | null>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return saved ? JSON.parse(saved).lastInsightId || null : null
    } catch {
      return null
    }
  })

  // Handler para cliques em botões de onboarding
  const handleButtonClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    
    // Procura pelo botão pai se o clique foi em um elemento filho
    let buttonElement = target
    if (target.tagName !== 'BUTTON') {
      buttonElement = target.closest('.rushmind-button') as HTMLElement
    }
    
    if (buttonElement && buttonElement.classList.contains('rushmind-button')) {
      e.preventDefault()
      const href = buttonElement.getAttribute('data-href')
      
      if (href?.includes('showCreateTask=true')) {
        handleOpenTaskModal()
      } else if (href) {
        window.location.href = href
      }
    }
  }

  // Handler para abrir modal de tarefa
  const handleOpenTaskModal = () => {
    setShowTaskModal(true)
  }

  // Handler para fechar modal de tarefa
  const handleCloseTaskModal = () => {
    setShowTaskModal(false)
  }

  const handleToggleMinimize = () => {
    const newState = !isMinimized
    setIsMinimized(newState)
    
    // Salva o estado no localStorage (incluindo lastInsightId)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        isMinimized: newState,
        lastInsightId: lastInsightId,
        timestamp: Date.now()
      }))
    } catch (error) {
      console.error('Erro ao salvar estado do RushMind:', error)
    }
  }

  const handleHeaderClick = () => {
    if (!isMinimized) {
      setIsMinimized(true)
      
      // Salva o estado no localStorage (incluindo lastInsightId)
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          isMinimized: true,
          lastInsightId: lastInsightId,
          timestamp: Date.now()
        }))
      } catch (error) {
        console.error('Erro ao salvar estado do RushMind:', error)
      }
    }
  }

  const handleFeedback = async (type: 'helpful' | 'not_helpful', reason?: string) => {
    if (!insight || !insight.id) {
      return
    }

    setFeedback(type)

    try {
      // Configurar token manualmente
      const auth = getAuth()
      if (auth?.api_token) {
        (axios.defaults.headers as any).Authorization = `Bearer ${auth.api_token}`
      }

      const payload = {
        ai_insight_id: insight.id,
        feedback_type: type,
        insight_type: insight.type,
        ...(reason ? {reason} : {}),
      }

      await axios.post(`${API_URL}/api/ai-insights/feedback`, payload)
    } catch (error: any) {
      // Mantém o feedback visualmente mesmo se houver erro no backend
    }
  }

  // Garante o Bearer token nas chamadas de cobrança (mesmo padrão do restante do widget).
  const applyAuthHeader = () => {
    const auth = getAuth()
    if (auth?.api_token) {
      ;(axios.defaults.headers as any).Authorization = `Bearer ${auth.api_token}`
    }
  }

  const removeNudge = (id: number) => {
    setNudgeState((prev) => ({...prev, [id]: {removed: true}}))
  }

  const setNudgeLoading = (id: number, loading: boolean) => {
    setNudgeState((prev) => ({...prev, [id]: {...prev[id], loading, message: undefined}}))
  }

  const setNudgeMessage = (id: number, message: string) => {
    setNudgeState((prev) => ({...prev, [id]: {...prev[id], loading: false, message}}))
  }

  const flashNudgeSuccess = (text: string) => {
    setNudgeSuccess(text)
    window.setTimeout(() => setNudgeSuccess(null), 2500)
  }

  // "Já resolvi": pede ao backend para reconciliar a cobrança contra o estado atual da tarefa.
  const handleNudgeCheck = async (id: number) => {
    if (nudgeState[id]?.loading) {
      return
    }
    setNudgeLoading(id, true)

    try {
      applyAuthHeader()
      const response = await axios.post(`${API_URL}/api/rushmind/nudges/${id}/check`)
      if (response.data?.resolved) {
        removeNudge(id)
        flashNudgeSuccess('Boa!')
      } else {
        setNudgeMessage(
          id,
          response.data?.message ||
            'Ainda não consegui confirmar. Atualize a tarefa e tente de novo.'
        )
      }
    } catch (error) {
      setNudgeMessage(id, 'Não foi possível concluir agora. Tente novamente mais tarde.')
    }
  }

  // "Ignorar": silencia a cobrança sem pedir motivo nesta versão.
  const handleNudgeDismiss = async (id: number) => {
    if (nudgeState[id]?.loading) {
      return
    }
    setNudgeLoading(id, true)

    try {
      applyAuthHeader()
      await axios.post(`${API_URL}/api/rushmind/nudges/${id}/dismiss`, {})
      removeNudge(id)
    } catch (error) {
      setNudgeMessage(id, 'Não foi possível concluir agora. Tente novamente mais tarde.')
    }
  }

  // Reseta feedback quando um novo insight é carregado
  useEffect(() => {
    setFeedback(null)
    setFeedbackReason('')
    setFeedbackReasonSent(false)
  }, [sessionTimestamp])

  const handleFeedbackReason = async () => {
    const reason = feedbackReason.trim()
    if (!reason) {
      return
    }
    setFeedbackReasonSent(true)
    await handleFeedback('not_helpful', reason)
  }

  // Aguarda a página principal carregar antes de habilitar o carregamento do insight
  // Prioriza o conteúdo principal da página sobre o widget RushMind
  useEffect(() => {
    // Delay inicial de 2 segundos para dar tempo da página carregar
    const initialDelay = setTimeout(() => {
      // Usa requestIdleCallback se disponível para aguardar o navegador estar ocioso
      // Fallback para setTimeout se não disponível
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(
          () => {
            // Aguarda também o documento estar completamente carregado
            if (document.readyState === 'complete') {
              setIsPageReady(true)
            } else {
              window.addEventListener('load', () => setIsPageReady(true), { once: true })
            }
          },
          { timeout: 3000 } // Timeout máximo de 3 segundos
        )
      } else {
        // Fallback: aguarda documento completo + delay adicional
        if (document.readyState === 'complete') {
          setTimeout(() => setIsPageReady(true), 500)
        } else {
          window.addEventListener('load', () => {
            setTimeout(() => setIsPageReady(true), 500)
          }, { once: true })
        }
      }
    }, 2000) // 2 segundos de delay inicial

    return () => {
      clearTimeout(initialDelay)
    }
  }, [])

  // Verificar se o RushMind deve ser exibido (OpenAI disponível + dados suficientes)
  const { 
    data: availabilityData,
    isLoading: availabilityLoading,
    error: availabilityError
  } = useQuery<RushMindAvailability>(['rushmind-availability'], checkRushMindAvailability, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 30, // 30 minutos
  })

  const isPlanBlockedWithData =
    availabilityData?.reason === 'plan_upgrade_required' &&
    availabilityData?.has_rushmind_data === true &&
    availabilityData?.has_rushmind_module === false

  const {
    data: insightData,
    isLoading: insightLoading,
    error: insightError
  } = useQuery(['rushmind-insight', sessionTimestamp], getRushMindInsight, {
    // Só habilita após a página principal estar carregada E disponibilidade confirmada
    // Isso prioriza o carregamento do conteúdo principal sobre o widget RushMind
    enabled:
      availabilityData?.should_show_rushmind === true &&
      !isPlanBlockedWithData &&
      isPageReady,
    refetchOnWindowFocus: false,
    retry: false, // Não tenta novamente em caso de erro
    retryOnMount: false, // Não tenta novamente ao montar componente
    refetchOnReconnect: false, // Não refaz ao reconectar
    staleTime: Infinity, // Mantém durante a sessão
    cacheTime: 0, // Limpa ao sair da página
  })

  // Widget agora disponível para TODOS os usuários (arquitetura híbrida)
  // Usuários comuns: insights pessoais (100%)
  // Gestores/Admins: insights gerenciais (70%) + pessoais (30%)

  // Detecta quando um novo insight é gerado e expande automaticamente o widget
  const insight: RushMindInsight | null = insightData?.success ? insightData.data : null

  // Cobranças ainda visíveis: exclui as que o usuário já resolveu/ignorou nesta sessão.
  // open_nudges pode vir undefined (resposta antiga em cache) — trata como lista vazia.
  const visibleNudges: RushMindNudge[] = (insight?.open_nudges ?? []).filter(
    (nudge) => !nudgeState[nudge.id]?.removed
  )

  // Recap "Sua semana": mostra no lugar do insight diário até o usuário fechar.
  // weekly_recap pode vir undefined/null — defensivo.
  const weeklyRecap: RushMindWeeklyRecap | null = insight?.weekly_recap ?? null
  const showWeeklyRecap = !!weeklyRecap && !weeklyRecapSeen

  const handleDismissWeeklyRecap = () => {
    try {
      localStorage.setItem(weeklyRecapSeenKey(), '1')
    } catch (error) {
      console.error('Erro ao salvar estado do recap semanal:', error)
    }
    setWeeklyRecapSeen(true)
  }

  // Limpa o estado local das cobranças ao trocar de insight
  useEffect(() => {
    setNudgeState({})
    setNudgeSuccess(null)
  }, [insight?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (insight && insight.id) {
      // Primeira vez que recebe um insight (inicialização)
      if (lastInsightId === null) {
        setLastInsightId(insight.id)
        
        // Salva o ID no localStorage
        try {
          const saved = localStorage.getItem(STORAGE_KEY)
          const current = saved ? JSON.parse(saved) : {}
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            ...current,
            lastInsightId: insight.id
          }))
        } catch (error) {
          console.error('Erro ao salvar lastInsightId:', error)
        }
        return
      }
      
      // Se é um novo insight (ID diferente do anterior)
      if (lastInsightId !== insight.id) {
        // Expande o widget automaticamente para mostrar o novo insight
        setIsMinimized(false)
        
        // Limpa o estado do localStorage e salva o novo ID
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            isMinimized: false,
            lastInsightId: insight.id,
            timestamp: Date.now()
          }))
        } catch (error) {
          console.error('Erro ao atualizar estado do RushMind:', error)
        }
        
        // Atualiza o ID do último insight
        setLastInsightId(insight.id)
      }
    }
  }, [insight?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Se está carregando a disponibilidade, não exibe nada
  if (availabilityLoading) {
    return null
  }

  // Se há erro na verificação de disponibilidade, não exibe nada
  if (availabilityError) {
    return null
  }

  // Sem módulo e com dados: esconder widget por completo
  if (isPlanBlockedWithData) {
    return null
  }

  // Determina se deve mostrar o RushMind ou o placeholder
  const shouldShowRushMind = availabilityData?.should_show_rushmind === true
  const showPlaceholder = !availabilityLoading && !availabilityError && !shouldShowRushMind

  // Se deve mostrar placeholder (instância sem dados suficientes)
  if (showPlaceholder) {
    return (
      <div className={`rushmind-widget ${isMinimized ? 'minimized' : 'expanded'}`}>
        {isMinimized ? (
          <button 
            className="rushmind-button-minimized"
            onClick={handleToggleMinimize}
            title="Abrir RushMind"
          >
            <i className="bi bi-info-circle text-gray-500 me-2"></i>
            <span className="rushmind-text">RushMind</span>
          </button>
        ) : (
          <div className="rushmind-panel">
            <div className="rushmind-header" onClick={handleHeaderClick}>
              <div className="rushmind-title">
                <i className="bi bi-info-circle text-gray-500 me-2"></i>
                <span>RushMind</span>
              </div>
              <div className="rushmind-controls">
                <button 
                  className="rushmind-minimize-btn"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleToggleMinimize()
                  }}
                  title="Minimizar"
                >
                  <i className="bi bi-dash-lg"></i>
                </button>
              </div>
            </div>
            
            <div className="rushmind-content">
              <div className="rushmind-greeting">
                <p className="mb-2">Olá, <strong>{currentUser?.name}</strong>! 👋</p>
              </div>
              
              <div className="rushmind-insight">
                <div className="rushmind-insight-body">
                  <p className="mb-0">
                    <i className="bi bi-info-circle text-gray-500 me-2"></i>
                    Você ainda não tem dados cadastrados suficientes. Cadastre projetos e tarefas para começar a receber insights valiosos.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Se deve mostrar o RushMind normal (instância com dados)
  if (!shouldShowRushMind) {
    return null
  }

  return (
    <>
      <div className={`rushmind-widget ${isMinimized ? 'minimized' : 'expanded'}`}>
      {isMinimized ? (
        <button 
          className="rushmind-button-minimized"
          onClick={handleToggleMinimize}
          title="Abrir RushMind"
        >
          <i className="bi bi-magic text-brand me-2 blink-icon"></i>
          <span className="rushmind-text">RushMind</span>
        </button>
      ) : (
        <div className="rushmind-panel">
          <div className="rushmind-header" onClick={handleHeaderClick}>
            <div className="rushmind-title">
              <i className="bi bi-magic text-brand me-2 blink-icon"></i>
              <span>RushMind</span>
            </div>
            <div className="rushmind-controls">
              <button 
                className="rushmind-minimize-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  handleToggleMinimize()
                }}
                title="Minimizar"
              >
                <i className="bi bi-dash-lg"></i>
              </button>
            </div>
          </div>
          
          <div className="rushmind-content">
                      <div className="rushmind-greeting">
                        <p className="mb-2">Olá, <strong>{currentUser?.name}</strong>! 👋</p>
                      </div>
            
            {insightLoading ? (
              <div className="rushmind-loading">
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <span>Gerando insight personalizado...</span>
                </div>
              </div>
            ) : insightError ? (
              <div className="rushmind-error">
                <div className="alert alert-warning mb-0">
                  <i className="ki-duotone ki-information-5 fs-2 me-2">
                    <span className="path1"></span>
                    <span className="path2"></span>
                    <span className="path3"></span>
                  </i>
                  <small>Erro ao carregar insight. Tente novamente mais tarde.</small>
                </div>
              </div>
            ) : insight ? (
              showWeeklyRecap && weeklyRecap ? (
                <div className='rushmind-recap'>
                  <div className='rushmind-recap-badge'>
                    <i className='bi bi-calendar-week me-2'></i>
                    Sua semana
                  </div>
                  <p
                    className='rushmind-recap-text mb-2'
                    dangerouslySetInnerHTML={{__html: processMarkdown(weeklyRecap.text)}}
                  />
                  <p className='rushmind-recap-streak mb-3'>
                    Sequência: <strong>{weeklyRecap.current_streak}</strong> dias · Recorde:{' '}
                    <strong>{weeklyRecap.best_streak}</strong> dias
                  </p>
                  <div className='rushmind-recap-actions'>
                    <Link
                      to='/meu-desempenho'
                      className='rushmind-recap-link'
                      onClick={handleDismissWeeklyRecap}
                    >
                      Ver meu desempenho
                    </Link>
                    <button
                      type='button'
                      className='rushmind-recap-ok'
                      onClick={handleDismissWeeklyRecap}
                    >
                      ok
                    </button>
                  </div>
                </div>
              ) : (
              <>
                <div className={`rushmind-insight ${insight.is_onboarding ? 'onboarding' : ''}`}>
                  <div className="rushmind-insight-body" onClick={handleButtonClick}>
                    {insight.is_onboarding && (
                      <div className="rushmind-onboarding-header">
                        <i className="bi bi-lightbulb rushmind-onboarding-tip-icon me-2"></i>
                        <span className="rushmind-onboarding-tip-title fw-bold">Dica do RushMind</span>
                      </div>
                    )}
                    <p 
                      className="mb-0"
                      dangerouslySetInnerHTML={{
                        __html: processMarkdown(insight.insight, insight.is_onboarding)
                      }}
                    />
                  </div>
                </div>

                {!insight.is_onboarding && !hideNudgesForInlineSection && (visibleNudges.length > 0 || nudgeSuccess) && (
                  <div className="rushmind-nudges">
                    <div className="rushmind-nudges-label">Cobranças</div>
                    {nudgeSuccess && (
                      <div className="rushmind-nudge-success">{nudgeSuccess}</div>
                    )}
                    {visibleNudges.length > 0 && (
                      <ul className="rushmind-nudge-list">
                        {visibleNudges.map((nudge) => {
                          const state = nudgeState[nudge.id] || {}
                          return (
                            <li key={nudge.id} className="rushmind-nudge-item">
                              <div className="rushmind-nudge-target">
                                {isSafeInternalPath(nudge.target_url) ? (
                                  <Link to={nudge.target_url} className="rushmind-nudge-link">
                                    {nudge.target_label}
                                  </Link>
                                ) : (
                                  <span>{nudge.target_label}</span>
                                )}
                              </div>
                              <div className="rushmind-nudge-actions">
                                <button
                                  type="button"
                                  className="rushmind-nudge-btn rushmind-nudge-btn-resolve"
                                  onClick={() => handleNudgeCheck(nudge.id)}
                                  disabled={!!state.loading}
                                >
                                  Já resolvi
                                </button>
                                <button
                                  type="button"
                                  className="rushmind-nudge-btn rushmind-nudge-btn-dismiss"
                                  onClick={() => handleNudgeDismiss(nudge.id)}
                                  disabled={!!state.loading}
                                >
                                  Ignorar
                                </button>
                              </div>
                              {state.message && (
                                <div className="rushmind-nudge-message">{state.message}</div>
                              )}
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                )}

         {insight.id && !insight.is_onboarding && (
           <div className="rushmind-feedback">
             <div className="rushmind-feedback-label">Este insight ajudou?</div>
             <div className="rushmind-feedback-buttons">
               <button
                 className={`rushmind-feedback-btn ${feedback === 'helpful' ? 'active-helpful' : ''}`}
                 onClick={() => handleFeedback('helpful')}
                 disabled={feedback !== null}
                 title="Útil"
               >
                 <i className="bi bi-hand-thumbs-up"></i>
               </button>
               <button
                 className={`rushmind-feedback-btn ${feedback === 'not_helpful' ? 'active-not-helpful' : ''}`}
                 onClick={() => handleFeedback('not_helpful')}
                 disabled={feedback !== null}
                 title="Não útil"
               >
                 <i className="bi bi-hand-thumbs-down"></i>
               </button>
             </div>
             {feedback && (
               <div className="rushmind-feedback-thanks">
                 Obrigado pelo feedback! 🙏
               </div>
             )}
             {feedback === 'not_helpful' && !feedbackReasonSent && (
               <form
                 className="rushmind-feedback-reason"
                 onSubmit={(e) => {
                   e.preventDefault()
                   handleFeedbackReason()
                 }}
               >
                 <input
                   type="text"
                   className="form-control form-control-sm"
                   placeholder="O que não ficou bom? (opcional)"
                   maxLength={300}
                   value={feedbackReason}
                   onChange={(e) => setFeedbackReason(e.target.value)}
                 />
                 <button type="submit" className="btn btn-sm btn-light" disabled={!feedbackReason.trim()}>
                   Enviar
                 </button>
               </form>
             )}
           </div>
         )}
              </>
              )
            ) : (
              <div className="rushmind-loading">
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <span>Processando seus dados...</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
      
      {/* Modal de criação de tarefa */}
      <TaskModal 
        show={showTaskModal}
        handleClose={handleCloseTaskModal}
      />
    </>
  )
}

export {RushMind}
